import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Shield, AlertTriangle, CheckCircle, Clock,
  ChevronDown, ChevronUp, Tag, List, BookOpen, Zap,
  AlertCircle, XCircle, Layers, Hash, Eye
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE as API } from '../../lib/apiBase';


const PHASE_LABELS = {
  pre_beta: { label: 'Pre-Beta', color: 'bg-red-500/20 text-red-300 border-red-500/30' },
  beta: { label: 'Beta Privada', color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  pre_public: { label: 'Pre-Publico', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  phase_2: { label: 'Fase 2', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  abogado: { label: 'Solo Abogado', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
  fiscal_local: { label: 'Asesor Fiscal', color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
};

const SEV = {
  critical: { label: 'CRITICO', color: 'text-red-400 bg-red-500/15 border-red-500/30' },
  high: { label: 'ALTO', color: 'text-amber-400 bg-amber-500/15 border-amber-500/30' },
  medium: { label: 'MEDIO', color: 'text-blue-400 bg-blue-500/15 border-blue-500/30' },
  low: { label: 'BAJO', color: 'text-slate-400 bg-slate-500/15 border-slate-500/30' },
};

export default function LegalOpsPanel() {
  const { token } = useAuth();
  const [tab, setTab] = useState('docs');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedDoc, setExpandedDoc] = useState(null);

  const fetchAll = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [docs, rules, matrix, naming, todo, copies] = await Promise.all([
        fetch(`${API}/api/legal-ops/documents`, { headers }).then(r => r.json()),
        fetch(`${API}/api/legal-ops/rules`, { headers }).then(r => r.json()),
        fetch(`${API}/api/legal-ops/feature-matrix`, { headers }).then(r => r.json()),
        fetch(`${API}/api/legal-ops/naming`, { headers }).then(r => r.json()),
        fetch(`${API}/api/legal-ops/todo`, { headers }).then(r => r.json()),
        fetch(`${API}/api/legal-ops/copies`, { headers }).then(r => r.json()),
      ]);
      setData({ docs, rules, matrix, naming, todo, copies });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <div className="text-center py-12 text-pulse-muted">Cargando Legal Ops...</div>;

  const tabs = [
    { id: 'docs', icon: FileText, label: 'Documentos', count: data.docs?.total },
    { id: 'rules', icon: Shield, label: 'Reglas', count: data.rules?.total },
    { id: 'matrix', icon: Layers, label: 'Matriz', count: data.matrix?.features?.length },
    { id: 'naming', icon: Tag, label: 'Naming', count: data.naming?.changes_needed },
    { id: 'todo', icon: List, label: 'TODO', count: data.todo?.todo?.length },
    { id: 'copies', icon: Eye, label: 'Copys UI', count: Object.keys(data.copies?.copies || {}).length },
  ];

  return (
    <div className="space-y-4" data-testid="legal-ops-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900/40 to-purple-900/30 rounded-xl p-5 border border-indigo-500/20">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <BookOpen size={22} className="text-indigo-400" />
          Legal Ops Center
        </h2>
        <p className="text-sm text-slate-400 mt-1">Paquete legal-operativo completo. Documentos, reglas, matriz de features y plan de implementacion.</p>
        <div className="flex gap-4 mt-3">
          <span className="text-xs bg-slate-800 px-2 py-1 rounded text-slate-300">{data.docs?.total || 0} documentos</span>
          <span className="text-xs bg-red-900/30 px-2 py-1 rounded text-red-300">{data.rules?.critical || 0} reglas criticas</span>
          <span className="text-xs bg-amber-900/30 px-2 py-1 rounded text-amber-300">{data.naming?.changes_needed || 0} cambios de naming</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900/60 rounded-xl p-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition ${
              tab === t.id ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/30' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`} data-testid={`legalops-tab-${t.id}`}>
            <t.icon size={14} />
            {t.label}
            {t.count !== undefined && <span className="bg-slate-700 px-1.5 py-0.5 rounded text-[10px]">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="min-h-[400px]">

        {/* === DOCUMENTS === */}
        {tab === 'docs' && data.docs?.documents && (
          <div className="space-y-2" data-testid="legal-docs-list">
            {data.docs.documents.map(doc => (
              <div key={doc.slug} className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden">
                <button onClick={() => setExpandedDoc(expandedDoc === doc.slug ? null : doc.slug)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-700/20 transition text-left">
                  <div className="flex items-center gap-3">
                    <FileText size={16} className="text-indigo-400 shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-white">{doc.title}</span>
                      <span className="ml-2 text-[10px] text-slate-500">v{doc.version}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.requires_acceptance && (
                      <span className="text-[10px] bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded border border-amber-500/20">
                        Requiere aceptacion: {doc.acceptance_trigger}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-500">{doc.sections_count} secciones</span>
                    {expandedDoc === doc.slug ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                  </div>
                </button>
                {expandedDoc === doc.slug && (
                  <div className="px-4 pb-4 space-y-3 border-t border-slate-700/30 pt-3">
                    {doc.sections.map(s => (
                      <div key={s.id} className="bg-slate-900/40 rounded-lg p-3">
                        <h4 className="text-xs font-bold text-slate-300 mb-1">{s.heading}</h4>
                        <p className="text-xs text-slate-400 leading-relaxed">{s.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* === RULES === */}
        {tab === 'rules' && data.rules?.rules && (
          <div className="space-y-2" data-testid="legal-rules-list">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-3 text-center">
                <span className="text-2xl font-bold text-red-400">{data.rules.critical}</span>
                <span className="block text-[10px] text-red-300 mt-0.5">Criticas</span>
              </div>
              <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-3 text-center">
                <span className="text-2xl font-bold text-amber-400">{data.rules.high}</span>
                <span className="block text-[10px] text-amber-300 mt-0.5">Altas</span>
              </div>
              <div className="bg-slate-800/50 border border-slate-700/30 rounded-lg p-3 text-center">
                <span className="text-2xl font-bold text-slate-300">{data.rules.total - data.rules.critical - data.rules.high}</span>
                <span className="block text-[10px] text-slate-400 mt-0.5">Otras</span>
              </div>
            </div>
            {data.rules.rules.map(r => {
              const sev = SEV[r.severity] || SEV.low;
              return (
                <div key={r.rule_id} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30 flex items-start gap-3">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${sev.color}`}>{sev.label}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] text-slate-600 font-mono">{r.rule_id}</span>
                      <span className="text-sm text-white">{r.description}</span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">{r.reason}</p>
                    <div className="flex gap-1 mt-1">
                      {r.scope.split(',').map(s => (
                        <span key={s} className="text-[10px] bg-slate-700/50 text-slate-400 px-1.5 py-0.5 rounded">{s.trim()}</span>
                      ))}
                      <span className="text-[10px] bg-indigo-500/10 text-indigo-300 px-1.5 py-0.5 rounded">{r.feature}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* === FEATURE MATRIX === */}
        {tab === 'matrix' && data.matrix?.features && (
          <div className="space-y-3" data-testid="legal-matrix">
            {data.matrix.features.map((f, i) => {
              const riskColor = f.risk === 'alto' ? 'text-red-400 bg-red-500/10' : f.risk === 'medio' ? 'text-amber-400 bg-amber-500/10' : 'text-green-400 bg-green-500/10';
              return (
                <div key={i} className="bg-slate-800/50 rounded-xl border border-slate-700/30 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-bold text-white">{f.feature}</h4>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${riskColor}`}>Riesgo {f.risk}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    <div className="bg-slate-900/40 rounded p-2">
                      <span className="text-slate-500 block mb-0.5">Naturaleza juridica</span>
                      <span className="text-slate-300">{f.legal_nature}</span>
                    </div>
                    <div className="bg-slate-900/40 rounded p-2">
                      <span className="text-slate-500 block mb-0.5">Riesgo</span>
                      <span className="text-slate-300">{f.risk_detail}</span>
                    </div>
                    <div className="bg-slate-900/40 rounded p-2">
                      <span className="text-slate-500 block mb-0.5">Copy/Disclaimer</span>
                      <span className="text-amber-300">{f.copy_needed}</span>
                    </div>
                    <div className="bg-slate-900/40 rounded p-2">
                      <span className="text-slate-500 block mb-0.5">Regla backend</span>
                      <span className="text-cyan-300">{f.backend_rule}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-[10px]">
                    <span className="text-slate-500">Bloqueo: </span>
                    <span className="text-red-300">{f.blocking}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* === NAMING === */}
        {tab === 'naming' && data.naming?.naming && (
          <div data-testid="legal-naming">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/30 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-700">
                    <th className="p-3">Termino actual</th>
                    <th className="p-3 text-center">Riesgo</th>
                    <th className="p-3 text-center">Decision</th>
                    <th className="p-3">Alternativa</th>
                    <th className="p-3">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {data.naming.naming.map((n, i) => {
                    const riskColor = n.risk === 'alto' ? 'text-red-400' : n.risk === 'medio' ? 'text-amber-400' : 'text-green-400';
                    return (
                      <tr key={i} className="border-b border-slate-700/30">
                        <td className="p-3 font-medium text-white">{n.current}</td>
                        <td className={`p-3 text-center font-bold ${riskColor}`}>{n.risk}</td>
                        <td className="p-3 text-center">
                          {n.keep
                            ? <CheckCircle size={14} className="text-green-400 inline" />
                            : <XCircle size={14} className="text-red-400 inline" />}
                          <span className="ml-1 text-[10px] text-slate-400">{n.keep ? 'Mantener' : 'Cambiar'}</span>
                        </td>
                        <td className="p-3">{n.keep ? <span className="text-slate-600">-</span> : <span className="text-green-300 font-medium">{n.alternative}</span>}</td>
                        <td className="p-3 text-slate-400 max-w-xs">{n.reason}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* === TODO === */}
        {tab === 'todo' && data.todo?.by_phase && (
          <div className="space-y-4" data-testid="legal-todo">
            {Object.entries(data.todo.by_phase).map(([phase, tasks]) => {
              const phaseInfo = PHASE_LABELS[phase] || { label: phase, color: 'bg-slate-500/20 text-slate-300' };
              return (
                <div key={phase}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-bold px-2 py-1 rounded border ${phaseInfo.color}`}>{phaseInfo.label}</span>
                    <span className="text-[10px] text-slate-500">{tasks.length} tareas</span>
                  </div>
                  <div className="space-y-1.5">
                    {tasks.map(t => (
                      <div key={t.id} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30 flex items-start gap-3">
                        <span className="text-[10px] font-mono text-slate-600 shrink-0 mt-0.5">{t.id}</span>
                        <div className="flex-1">
                          <p className="text-sm text-white">{t.task}</p>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            <span className="text-[10px] bg-slate-700/50 text-slate-400 px-1.5 py-0.5 rounded">{t.owner}</span>
                            <span className="text-[10px] bg-slate-700/50 text-slate-400 px-1.5 py-0.5 rounded">Dificultad: {t.difficulty}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                              t.urgency === 'critica' ? 'text-red-300 bg-red-500/10 border-red-500/20' :
                              t.urgency === 'alta' ? 'text-amber-300 bg-amber-500/10 border-amber-500/20' :
                              'text-slate-400 bg-slate-500/10 border-slate-500/20'
                            }`}>{t.urgency}</span>
                            {t.depends_on && <span className="text-[10px] text-slate-600">Depende: {t.depends_on}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* === UI COPIES === */}
        {tab === 'copies' && data.copies?.copies && (
          <div className="space-y-3" data-testid="legal-copies">
            {Object.entries(data.copies.copies).map(([key, copy]) => (
              <div key={key} className="bg-slate-800/50 rounded-xl border border-slate-700/30 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Hash size={14} className="text-indigo-400" />
                  <span className="text-[10px] font-mono text-slate-500">{key}</span>
                </div>
                <h4 className="text-sm font-bold text-white">{copy.title}</h4>
                <p className="text-xs text-slate-400 mt-0.5">{copy.subtitle}</p>
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5 mt-2">
                  <span className="text-[10px] text-amber-500 font-bold block mb-0.5">Disclaimer:</span>
                  <p className="text-xs text-amber-200/80">{copy.disclaimer}</p>
                </div>
                {copy.cta && (
                  <div className="mt-2">
                    <span className="text-[10px] text-slate-500">CTA: </span>
                    <span className="text-xs text-indigo-300 font-medium">{copy.cta}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
